import { createAsyncThunk } from '@reduxjs/toolkit'
import _ from 'lodash'
import addressServiceAPI from '../apis/AddressServiceAPI'
import { LocationData } from '../components/map/MapComponent'
import RecaptchaRequiredError from '../errors/RecaptchaRequiredError'
import { forceManualRecaptcha, RecaptchaAction, RecaptchaPayload } from './slice'
import { RootState } from './store'
import { PayloadAction } from './types'

export const GET_CANDIDATE_ADDRESSES = 'GET_CANDIDATE_ADDRESSES'
export const UPDATE_CANDIDATE_ADDRESSES_DATA = 'UPDATE_CANDIDATE_ADDRESSES_DATA'

export interface AddressSearchResultsData extends LocationData {
  LAIS_KEY: string
  LATITUDE: number
  LONGITUDE: number
  STATE?: string      /* optional, only address search has this, placenames does not */
  SearchTerm: string
  GNAF_PID?: string   /* optional, placenames search does not have this field */
  Result?: string
  PARCEL_ALIAS?: string
  SEARCH_TYPE?: string  /* this should be mandatory when we use the combined placenames/address search everywhere */
  PLACE_ID?: number /* optional, only for placenames */
}

type CandidateAddressesData = {
  isLoading: boolean
  parcels: AddressSearchResultsData[]
}

export const getCandidateAddressesBase = (state: RootState) => {
  return state.candidateAddresses
}

export const getAddresses = (state: RootState) => {
  return getCandidateAddressesBase(state).parcels
}

export const areAddressesLoading = (state: RootState) => {
  return getCandidateAddressesBase(state).isLoading
}

export interface CandidateAddressSearch extends RecaptchaPayload {
  searchTerm: string
}

const cleanParcels = (parcels: AddressSearchResultsData[]) => {
  // For address data coming back for street addresses/parcels, we'll get data that includes
  // addresses outside the NT, so we want to filter those out.
  // For placename data we'll get back data that doesn't have a state field at all, so we'll accept those
  // records if there state field is undefined.
  // In all scenarios though we will get a LAISKEY
  return parcels && parcels.filter((parcel) => {
    return parcel.LAIS_KEY && (parcel.STATE === 'NT' || parcel.STATE === undefined)
  })
}

export const getCandidateAddresses = createAsyncThunk(
  GET_CANDIDATE_ADDRESSES,
  async (searchTerm: string, thunkAPI) => {
    try {
      const payload = await addressServiceAPI.fetchCandidateAddresses({ searchTerm })
      if (payload) {
        thunkAPI.dispatch(updateCandidateAddressesData(payload))
      }
    } catch (e) {
      if (e instanceof RecaptchaRequiredError) {
        const action: RecaptchaAction<CandidateAddressSearch> = {
          type: GET_CANDIDATE_ADDRESSES,
          payload: {
            searchTerm,
          },
        }
        thunkAPI.dispatch(forceManualRecaptcha(action))
      } else {
        throw e
      }
    }
  },
)

export const updateCandidateAddressesData = (results: AddressSearchResultsData[]): PayloadAction<AddressSearchResultsData[]> => ({
  type: UPDATE_CANDIDATE_ADDRESSES_DATA,
  payload: results,
})

export const candidateAddressesReducer = (state: CandidateAddressesData = { isLoading: false, parcels: [] }, {
  type,
  payload,
}: { type: string, payload: AddressSearchResultsData[] }) => {
  if (type === UPDATE_CANDIDATE_ADDRESSES_DATA) {
    let parcels = cleanParcels(payload)

    if (_.isEmpty(parcels)) {
      parcels = []
    }
    state = { ...state, parcels, isLoading: false }
  } else if (type === GET_CANDIDATE_ADDRESSES) {
    state = { ...state, isLoading: true }
  } else if (type === `${GET_CANDIDATE_ADDRESSES}/pending`) {
    state = { ...state, isLoading: true }
  }
  return state
}
